import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MenuItem, Switch, TextField, Tooltip } from "@mui/material";
import {
  GridColDef,
  GridRenderEditCellParams,
  useGridApiContext,
} from "@mui/x-data-grid";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import { DataSubType } from "oschema";

import {
  Field,
  InputKind,
  enumOptions,
  inputKindFor,
  isNativeDate,
  isNativeNumber,
  looksLikeUrl,
} from "../lib/schema-field";
import { prettyJson } from "../lib/format";
import {
  parsePercentInput,
  toDateInput,
  toDateTimeInput,
  toPercentInput,
  toTimeInput,
} from "./DynamicFormField";

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    "@media (max-width: 639px), (pointer: coarse)": { minHeight: 44 },
  },
  "& .MuiOutlinedInput-input": {
    fontSize: "0.8125rem",
    padding: "4px 8px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--od-interactive-boundary)",
  },
};

const asBoolean = (value: unknown): boolean =>
  value === true || value === "true" || value === 1 || value === "1";

const parseNativeNumber = (raw: string): number | null | string => {
  if (raw === "") return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? raw : parsed;
};

const useEditFocus = <T extends HTMLElement>(hasFocus: boolean) => {
  const ref = useRef<T | null>(null);
  useLayoutEffect(() => {
    if (hasFocus) ref.current?.focus();
  }, [hasFocus]);
  return ref;
};

const hexForPicker = (value: unknown): string => {
  const str = String(value ?? "").trim();
  if (/^#[0-9a-f]{6}$/i.test(str)) return str;
  if (/^[0-9a-f]{6}$/i.test(str)) return `#${str}`;
  return "";
};

type CellProps = GridRenderEditCellParams & {
  fieldDef: Field;
};

type KindProps = CellProps & {
  kind: InputKind;
};

const useCellCommit = (id: GridRenderEditCellParams["id"], field: string) => {
  const apiRef = useGridApiContext();
  const commit = (next: unknown) => {
    apiRef.current.setEditCellValue({ id, field, value: next });
  };
  const commitAndStop = async (next: unknown) => {
    const ok = await apiRef.current.setEditCellValue({
      id,
      field,
      value: next,
    });
    if (ok) {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };
  return { commit, commitAndStop };
};

const BooleanEdit: React.FC<CellProps> = ({
  id,
  field,
  value,
  hasFocus,
  colDef,
}) => {
  const { commitAndStop } = useCellCommit(id, field);
  const checked = asBoolean(value);
  const aria = String(colDef.headerName || field);
  return (
    <label className="flex h-full w-full items-center gap-2 px-1">
      <Switch
        size="small"
        checked={checked}
        autoFocus={hasFocus}
        inputProps={{ "aria-label": aria }}
        onChange={(event) => {
          void commitAndStop(event.target.checked);
        }}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: "var(--od-primary)",
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "var(--od-primary)",
          },
        }}
      />
      <span
        className={checked ? "text-xs text-od-text" : "text-xs text-od-muted"}
      >
        {checked ? "Yes" : "No"}
      </span>
    </label>
  );
};

const EnumEdit: React.FC<CellProps> = ({
  id,
  field,
  value,
  hasFocus,
  fieldDef,
  colDef,
}) => {
  const { commitAndStop } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLInputElement>(hasFocus);
  const options = enumOptions(fieldDef) ?? [];
  const nullable = fieldDef.nullable !== false;
  const current = value == null ? "" : String(value);
  const known = options.includes(current) ? current : "";
  return (
    <TextField
      select
      fullWidth
      size="small"
      value={known}
      inputRef={inputRef}
      inputProps={{ "aria-label": String(colDef.headerName || field) }}
      sx={inputSx}
      onChange={(event) => {
        const raw = event.target.value;
        void commitAndStop(raw === "" ? null : raw);
      }}
    >
      {nullable && (
        <MenuItem value="">
          <span className="text-od-muted">Not set</span>
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );
};

const NumberEdit: React.FC<KindProps> = ({
  id,
  field,
  value,
  hasFocus,
  fieldDef,
  kind,
  colDef,
}) => {
  const { commit } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLInputElement>(hasFocus);
  const native = isNativeNumber(fieldDef);
  const step =
    native && fieldDef.subType === DataSubType.INTEGER
      ? 1
      : native
        ? "any"
        : undefined;
  const display =
    kind === "percentage"
      ? toPercentInput(value, fieldDef)
      : value == null
        ? ""
        : String(value);
  return (
    <div className="flex h-full w-full items-center gap-1 px-1">
      {kind === "currency" && (
        <span className="shrink-0 text-xs text-od-muted" aria-hidden>
          $
        </span>
      )}
      <input
        ref={inputRef}
        className="h-8 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11 min-w-0 flex-1 rounded-md border border-od-interactive-boundary bg-od-surface px-2 text-xs text-od-text outline-none focus-visible:border-od-primary"
        type={native ? "number" : "text"}
        inputMode="decimal"
        step={step}
        value={display}
        aria-label={String(colDef.headerName || field)}
        onWheel={(event) => {
          event.currentTarget.blur();
        }}
        onChange={(event) => {
          const raw = event.target.value;
          if (kind === "percentage") {
            commit(parsePercentInput(raw, fieldDef, native));
            return;
          }
          commit(native ? parseNativeNumber(raw) : raw === "" ? null : raw);
        }}
      />
      {kind === "percentage" && (
        <span className="shrink-0 text-xs text-od-muted" aria-hidden>
          %
        </span>
      )}
    </div>
  );
};

const TemporalEdit: React.FC<KindProps> = ({
  id,
  field,
  value,
  hasFocus,
  fieldDef,
  kind,
  colDef,
}) => {
  const { commit } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLInputElement>(hasFocus);
  const convert =
    kind === "date"
      ? toDateInput
      : kind === "datetime"
        ? toDateTimeInput
        : toTimeInput;
  const converted = convert(value);
  const untrustedTemporal = kind !== "time" && !isNativeDate(fieldDef);
  const unparseable = value != null && value !== "" && converted === "";
  const native = !untrustedTemporal && !unparseable;
  const display = native ? converted : value == null ? "" : String(value);
  const inputType = native
    ? kind === "date"
      ? "date"
      : kind === "datetime"
        ? "datetime-local"
        : "time"
    : "text";
  return (
    <input
      ref={inputRef}
      className="h-8 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11 w-full rounded-md border border-od-interactive-boundary bg-od-surface px-2 text-xs text-od-text outline-none focus-visible:border-od-primary"
      type={inputType}
      value={display}
      aria-label={String(colDef.headerName || field)}
      onChange={(event) => {
        const raw = event.target.value;
        commit(raw === "" ? null : raw);
      }}
    />
  );
};

const JsonEdit: React.FC<CellProps> = ({
  id,
  field,
  value,
  hasFocus,
  colDef,
}) => {
  const apiRef = useGridApiContext();
  const inputRef = useEditFocus<HTMLTextAreaElement>(hasFocus);
  const [raw, setRaw] = useState(() => prettyJson(value));
  const invalid = useMemo(() => {
    if (raw.trim() === "") return false;
    try {
      JSON.parse(raw);
      return false;
    } catch {
      return true;
    }
  }, [raw]);

  return (
    <textarea
      ref={inputRef}
      spellCheck={false}
      rows={6}
      className="box-border h-full min-h-[6rem] w-full resize-y bg-transparent p-1.5 font-mono text-xs leading-4 text-od-text outline-none"
      style={invalid ? { color: "var(--od-danger)" } : undefined}
      value={raw}
      aria-label={String(colDef.headerName || field)}
      onChange={(event) => {
        const text = event.target.value;
        setRaw(text);
        if (text.trim() === "") {
          apiRef.current.setEditCellValue({ id, field, value: null });
          return;
        }
        try {
          apiRef.current.setEditCellValue({
            id,
            field,
            value: JSON.parse(text),
          });
        } catch {
          apiRef.current.setEditCellValue({ id, field, value: text });
        }
      }}
    />
  );
};

const LongTextEdit: React.FC<CellProps> = ({
  id,
  field,
  value,
  hasFocus,
  colDef,
}) => {
  const { commit } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLTextAreaElement>(hasFocus);
  return (
    <textarea
      ref={inputRef}
      spellCheck={false}
      rows={4}
      className="box-border h-full min-h-[4.5rem] w-full resize-y bg-transparent p-1.5 text-xs leading-4 text-od-text outline-none"
      value={value == null ? "" : String(value)}
      aria-label={String(colDef.headerName || field)}
      onChange={(event) => {
        commit(event.target.value);
      }}
    />
  );
};

const ColorEdit: React.FC<CellProps> = ({
  id,
  field,
  value,
  hasFocus,
  colDef,
}) => {
  const { commit } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLInputElement>(hasFocus);
  const display = value == null ? "" : String(value);
  const hex = hexForPicker(value);
  return (
    <div className="flex h-full w-full items-center gap-1 px-1">
      <input
        type="color"
        aria-label="Colour picker"
        className="h-8 w-8 max-sm:min-h-11 max-sm:min-w-11 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 shrink-0 cursor-pointer rounded-md border border-od-interactive-boundary bg-od-surface p-0.5"
        value={hex || "#000000"}
        onChange={(event) => commit(event.target.value)}
      />
      <input
        ref={inputRef}
        className="h-8 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11 min-w-0 flex-1 rounded-md border border-od-interactive-boundary bg-od-surface px-2 font-mono text-xs text-od-text outline-none focus-visible:border-od-primary"
        type="text"
        placeholder="#4f46e5"
        value={display}
        aria-label={String(colDef.headerName || field)}
        onChange={(event) =>
          commit(event.target.value === "" ? null : event.target.value)
        }
      />
    </div>
  );
};

const TextEdit: React.FC<KindProps> = ({
  id,
  field,
  value,
  hasFocus,
  kind,
  colDef,
}) => {
  const { commit } = useCellCommit(id, field);
  const inputRef = useEditFocus<HTMLInputElement>(hasFocus);
  const display = value == null ? "" : String(value);
  const showOpen = kind === "url" && looksLikeUrl(display);
  const href = display.trim();
  return (
    <div className="flex h-full w-full items-center gap-1 px-1">
      <input
        ref={inputRef}
        className="h-8 max-sm:min-h-11 [@media(pointer:coarse)]:min-h-11 min-w-0 flex-1 rounded-md border border-od-interactive-boundary bg-od-surface px-2 text-xs text-od-text outline-none focus-visible:border-od-primary"
        type="text"
        inputMode={kind === "phone" ? "tel" : undefined}
        autoComplete="off"
        value={display}
        aria-label={String(colDef.headerName || field)}
        onChange={(event) => {
          const raw = event.target.value;
          commit(raw === "" ? null : raw);
        }}
      />
      {showOpen && (
        <Tooltip title="Open link">
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Open link"
            className="inline-flex h-7 w-7 max-sm:min-h-11 max-sm:min-w-11 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 shrink-0 items-center justify-center rounded-md text-od-muted hover:text-od-text"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 15 }} />
          </a>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * One in-place editor for every `InputKind`. `inputKindFor` picks the control;
 * DataGrid still owns `processRowUpdate`. Password is opted out via
 * `isUnsafeInlineEdit` so it never reaches this cell.
 */
export const GridEditCell: React.FC<CellProps> = (params) => {
  const kind = inputKindFor(params.fieldDef);
  switch (kind) {
    case "boolean":
      return <BooleanEdit {...params} />;
    case "enum":
      return <EnumEdit {...params} />;
    case "number":
    case "currency":
    case "percentage":
      return <NumberEdit {...params} kind={kind} />;
    case "date":
    case "datetime":
    case "time":
      return <TemporalEdit {...params} kind={kind} />;
    case "json":
      return <JsonEdit {...params} />;
    case "longtext":
      return <LongTextEdit {...params} />;
    case "color":
      return <ColorEdit {...params} />;
    case "email":
    case "url":
    case "phone":
    case "text":
      return <TextEdit {...params} kind={kind} />;
    case "password":
    default:
      return null;
  }
};

const booleanParser = (value: unknown): boolean | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return asBoolean(value);
};

const nativeNumberParser = (value: unknown): number | null | string => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  return parseNativeNumber(String(value));
};

/**
 * `GridColDef` extras for in-place editing. Every kind shares `GridEditCell`;
 * relation keys stay form-only; password is not safe in a one-line cell.
 */
export const gridColEditProps = (field: Field): Partial<GridColDef> => {
  const kind = inputKindFor(field);
  if (kind === "password") return {};

  const renderEditCell = (params: GridRenderEditCellParams) => (
    <GridEditCell {...params} fieldDef={field} />
  );

  switch (kind) {
    case "boolean":
      return {
        type: "boolean",
        valueParser: booleanParser,
        renderEditCell,
      };
    case "enum": {
      const options = enumOptions(field) ?? [];
      const nullable = field.nullable !== false;
      return {
        type: "singleSelect",
        valueOptions: nullable ? ["", ...options] : options,
        renderEditCell,
      };
    }
    case "number":
    case "currency":
    case "percentage": {
      const native = isNativeNumber(field);
      return {
        ...(native
          ? { type: "number" as const, valueParser: nativeNumberParser }
          : {}),
        renderEditCell,
      };
    }
    default:
      return { renderEditCell };
  }
};

/** Kinds that look editable but would corrupt or leak the stored value. */
export const isUnsafeInlineEdit = (field: Field): boolean => {
  const kind = inputKindFor(field);
  return kind === "password";
};
